import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  Archive, ArchiveRestore, ArrowDown, ArrowLeft, ArrowUp, Check, ChevronDown, CornerDownRight, Download, Eye, FileText,
  HardDrive, RefreshCw, RotateCw, Scissors, Search, Trash2, Unlink, Upload, X,
} from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import TranscriptDownloads from '../components/TranscriptDownloads';
import ShareLinkManager from '../components/ShareLinkManager';
import { FaceCard, useFaceSave } from '../components/FaceNamer';
import { ACCEPT_ATTR, AUDIO_EXTS, VIDEO_EXTS, isMediaFile, mediaKindOf, extLabel } from '../lib/mediaKind';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import Checkbox from '../ui/Checkbox';
import ExpandedRowPanel from '../ui/ExpandedRowPanel';
import Grid from '../ui/Grid';
import Icon from '../ui/Icon';
import Input from '../ui/Input';
import ListRow from '../ui/ListRow';
import Modal from '../ui/Modal';
import Select from '../ui/Select';
import Skeleton from '../ui/Skeleton';
import Tag from '../ui/Tag';
import { useConfirm } from '../ui/Confirm';
import { useToast } from '../ui/Toast';
import { usePageHeader } from '../shell/pageHeader';
import { clean } from '../shell/copy';
import { enqueueUpload, useUploadDone } from '../shell/uploads';

// Raw footage on src/ui (HD-6; DESIGN rev 2.1 §3.1: Grid at medium density, Expanded-row panel, the global Upload sheet).
// Same calls as before. List: GET /api/footage?limit=100&offset&client_id&match_status|view=archived&uploaded_by, walked page
// by page until the total is in (never for external accounts: the API refuses them the list; their upload stays), polled
// every 8 s while a row transcribes; GET /api/clients (active), GET /api/team-members; POST /api/transcriptions (row and
// panel); DELETE /api/footage/:id (bulk, one by one, refusals skipped and listed). Upload: the footage multipart group
// (/api/footage/multipart/start | sign-part | complete | abort, startBody { clientId }) then POST /api/footage/confirm.
// Panel: GET /api/footage/:id, GET /api/transcriptions/raw_footage/:id (8 s poll), GET /api/client-characters, faces
// (components/FaceNamer), GET /api/adc/:id/status (5 s poll) + POST /api/adc/:id/process, PATCH .../matches/:id/confirm|reject,
// POST .../no-match | manual-match | unassign | match, GET /api/footage/scripts-search, GET /api/scripts/:id, GET .../download,
// PATCH /api/footage/:id { clientId | originalExt }, GET .../archive-preview, PATCH .../archive | unarchive, DELETE /api/footage/:id,
// share links (components/ShareLinkManager), transcript downloads (components/TranscriptDownloads).

const EDIT_ROLES = ['Editor', 'Producer', 'Post Lead', 'Post_Lead', 'Power User', 'Power_User', 'Admin'];
const ADMIN_ROLES = ['Admin', 'Power User', 'Power_User'];
const hasAnyRole = (user, roles) => !!user?.roles?.some(r => roles.includes(r));

// The status filter. "archived" is not a match status: it maps to view=archived.
const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'unmatched', label: 'Unmatched' },
  { value: 'pending_review', label: 'Needs review' },
  { value: 'matched', label: 'Matched' },
  { value: 'no_match', label: 'No match' },
  { value: 'archived', label: 'Archived' },
];
const MATCH_LABEL = { unmatched: 'Unmatched', pending_review: 'Needs review', matched: 'Matched', no_match: 'No match' };
// Match-status sort: most actionable first. Transcribed sort: not transcribed first, then in progress, failed, done.
const MATCH_STATUS_RANK = { pending_review: 0, unmatched: 1, matched: 2, no_match: 3 };
const TRANSCRIBED_RANK = { failed: 2, pending: 1, processing: 1, complete: 3 };
const SUPPORTED = `Video: ${VIDEO_EXTS.map(e => e.toUpperCase()).join(', ')}. Audio: ${AUDIO_EXTS.map(e => e.toUpperCase()).join(', ')}.`;
const FOOTAGE_ENDPOINTS = {
  start:    '/api/footage/multipart/start',
  signPart: '/api/footage/multipart/sign-part',
  complete: '/api/footage/multipart/complete',
  abort:    '/api/footage/multipart/abort',
};

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
function formatBytes(b) {
  const n = Number(b);
  if (!n) return '';
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
function fmtDate(str) {
  if (!str) return '';
  return new Date(str).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
const formatPct = n => `${Math.round(Number(n) * 100)}%`;
const isUnverified = c => c.uploadStatus === 'failed' || c.verificationStatus === 'mismatch' || c.verificationStatus === 'missing';
const joinMeta = parts => parts.filter(Boolean).map(clean).join(', ');
const stop = e => e.stopPropagation();

// The list refresh the upload confirm step calls when a file fails verification (the page may have unmounted by then).
const listRefresh = { current: null };

// ── Small cells ───────────────────────────────────────────────────────────────

// A clip's still: the thumbnail, or for audio (no frame to show) a flat tile carrying the extension, never a broken poster.
function Thumb({ clip }) {
  if (clip.thumbnailUrl) return <img className="ui-thumb" src={clip.thumbnailUrl} alt="" />;
  if (mediaKindOf(clip) === 'audio') return <span className="ui-thumb ui-thumb--label" title={clean(clip.fileName)}>{extLabel(clip.fileName)}</span>;
  return <span className="ui-thumb" />;
}

function MatchStatus({ clip }) {
  const s = clip.matchStatus;
  if (s === 'matched') return <Badge tone="ok">Matched</Badge>;
  if (s === 'pending_review') return <Badge tone="warn">{`Needs review${clip.pendingMatchCount > 0 ? ` (${clip.pendingMatchCount})` : ''}`}</Badge>;
  if (s === 'no_match') return <span className="ui-meta">No match</span>;
  if (s === 'unmatched') return <span>Unmatched</span>;
  return <span>{clean(MATCH_LABEL[s] || s || '')}</span>;
}

function SortHead({ col, label, sort, onSort }) {
  const active = sort.col === col;
  return (
    <button type="button" className="ui-sort" onClick={() => onSort(col)}
      aria-label={active ? `${label}, sorted ${sort.dir === 'asc' ? 'ascending' : 'descending'}` : `Sort by ${label}`}>
      {label}
      {active && <Icon icon={sort.dir === 'asc' ? ArrowUp : ArrowDown} />}
    </button>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function RawFootage() {
  const { user } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const [footage,        setFootage]        = useState([]);
  const [total,          setTotal]          = useState(0);
  const [loading,        setLoading]        = useState(true);
  const [loadError,      setLoadError]      = useState(false);
  const [clients,        setClients]        = useState([]);
  const [members,        setMembers]        = useState([]);
  const [clientFilter,   setClientFilter]   = useState('');
  const [statusFilter,   setStatusFilter]   = useState('');
  const [uploaderFilter, setUploaderFilter] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  // Seed the open clip from the URL so a refresh stays on that clip.
  const [selectedId,     setSelectedId]     = useState(() => searchParams.get('footage') || null);
  const [uploadOpen,     setUploadOpen]     = useState(false);
  const [sort,           setSort]           = useState({ col: 'date', dir: 'desc' });
  const [transcribingIds, setTranscribingIds] = useState(() => new Set());

  // Mirror the open clip into the URL (?footage=<id>) so a refresh restores it.
  useEffect(() => {
    setSearchParams(prev => {
      const p = new URLSearchParams(prev);
      if (selectedId) p.set('footage', selectedId); else p.delete('footage');
      return p;
    }, { replace: true });
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const isExternal = user?.isExternal;
  const canUpload  = hasAnyRole(user, EDIT_ROLES) || isExternal;
  // Camera-drive ingest: the upload roles minus external users (the API enforces the same set).
  const canIngest  = !isExternal && hasAnyRole(user, EDIT_ROLES);

  // Bulk delete is PERMANENT (the cloud file and its thumbnail go; no recycle bin). Per-row rights (Admin, the uploader,
  // editor cross-delete) are enforced by the API; refusals are skipped and listed, never forced.
  const [selectedClips,  setSelectedClips]  = useState(new Set());
  const [bulkDeleting,   setBulkDeleting]   = useState(false);
  const [bulkReport,     setBulkReport]     = useState(null);   // { deleted, total, skipped: [{ name, reason }] }
  useEffect(() => {
    setSelectedClips(prev => {
      if (prev.size === 0) return prev;
      const visible = new Set(footage.map(c => c.id));
      const next = new Set([...prev].filter(id => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [footage]);
  function toggleClipSelect(id) {
    setSelectedClips(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  // `silent` keeps the rows on screen: background polls must not blank the list to a skeleton every few seconds.
  const fetchFootage = useCallback(async ({ silent = false } = {}) => {
    // The API refuses external accounts the footage list (404 on every internal footage read); they can still upload.
    if (isExternal) { setFootage([]); setTotal(0); setLoading(false); return; }
    if (!silent) { setLoading(true); setLoadError(false); }
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (clientFilter)   params.set('client_id',   clientFilter);
      if (statusFilter === 'archived') params.set('view', 'archived');
      else if (statusFilter)           params.set('match_status', statusFilter);
      if (uploaderFilter) params.set('uploaded_by',  uploaderFilter);
      // DEF-113: the API caps a page at 100. Walk every page (newest first) until the total is in.
      const all = []; let count = 0, offset = 0;
      for (let page = 0; page < 50; page++) {
        params.set('offset', String(offset));
        const r = await api.get(`/api/footage?${params}`);
        const rows = r.data.footage || []; count = r.data.total || 0;
        all.push(...rows); offset += rows.length;
        if (rows.length < 100 || all.length >= count) break;
      }
      setFootage(all);
      setTotal(count);
    } catch {
      if (!silent) setLoadError(true);
    } finally { if (!silent) setLoading(false); }
  }, [clientFilter, statusFilter, uploaderFilter, isExternal]);

  useEffect(() => { fetchFootage(); }, [fetchFootage]);
  useEffect(() => { listRefresh.current = () => fetchFootage(); return () => { listRefresh.current = null; }; }, [fetchFootage]);
  useUploadDone(item => { if (item.meta?.kind === 'footage') fetchFootage(); });
  const refreshList = useCallback(() => { fetchFootage(); }, [fetchFootage]);

  useEffect(() => {
    api.get('/api/clients').then(r => setClients(r.data.filter(c => c.isActive))).catch(() => {});
    api.get('/api/team-members').then(r => setMembers(r.data)).catch(() => {});
  }, []);

  // Poll the list while any row transcribes, so the column updates without a manual refresh.
  const anyTranscribing = useMemo(
    () => footage.some(c => c.transcriptionStatus === 'pending' || c.transcriptionStatus === 'processing'),
    [footage],
  );
  useEffect(() => {
    if (!anyTranscribing) return undefined;
    const id = setInterval(() => { fetchFootage({ silent: true }); }, 8000);
    return () => clearInterval(id);
  }, [anyTranscribing, fetchFootage]);

  async function handleTranscribeRow(clipId) {
    if (transcribingIds.has(clipId)) return;
    setTranscribingIds(prev => { const next = new Set(prev); next.add(clipId); return next; });
    // Optimistic: the row reads "Transcribing" straight away.
    setFootage(prev => prev.map(c => c.id === clipId ? { ...c, transcriptionStatus: 'pending' } : c));
    try {
      await api.post('/api/transcriptions', { source_type: 'raw_footage', source_id: clipId });
    } catch (err) {
      toast(clean(err.response?.data?.error) || 'The transcription did not start. Try again.', { tone: 'danger' });
      setFootage(prev => prev.map(c => c.id === clipId ? { ...c, transcriptionStatus: c.transcriptionStatus === 'pending' ? null : c.transcriptionStatus } : c));
    } finally {
      setTranscribingIds(prev => { const next = new Set(prev); next.delete(clipId); return next; });
    }
  }

  async function askBulkDelete() {
    const n = selectedClips.size;
    if (!n || bulkDeleting) return;
    const ok = await confirm({
      title: `Permanently delete ${plural(n, 'clip')}?`,
      body: 'This deletes the files and their thumbnails from cloud storage and removes them from Raw footage. Unlike Storage, there is no recycle bin here: this cannot be undone. Clips you are not allowed to delete are skipped and listed.',
      confirmLabel: `Delete ${plural(n, 'clip')}`,
      destructive: true,
      typed: 'delete',
    });
    if (ok) handleBulkDelete();
  }

  async function handleBulkDelete() {
    const ids = [...selectedClips];
    if (!ids.length || bulkDeleting) return;
    setBulkDeleting(true);
    setBulkReport(null);
    let deleted = 0;
    const skipped = [];
    for (const id of ids) {
      try {
        await api.delete(`/api/footage/${id}`);
        deleted++;
      } catch (err) {
        const c = footage.find(x => x.id === id);
        skipped.push({ id, name: c?.fileName || id, reason: err.response?.data?.error || 'The delete failed.' });
      }
    }
    setBulkDeleting(false);
    setSelectedClips(new Set());
    if (selectedId && ids.includes(selectedId) && !skipped.some(s => s.id === selectedId)) setSelectedId(null);
    fetchFootage();
    if (skipped.length) {
      setBulkReport({ deleted, total: ids.length, skipped });
      toast(`${deleted} of ${ids.length} deleted. ${skipped.length} skipped; the list above the clips says why.`, { tone: 'warn' });
    } else {
      toast(`${plural(deleted, 'clip')} deleted.`);
    }
  }

  const sortedFootage = useMemo(() => {
    const dir = sort.dir === 'asc' ? 1 : -1;
    const key = (c) => {
      switch (sort.col) {
        case 'file':        return (c.fileName || '').toLowerCase();
        case 'project':     return (c.clientName || '').toLowerCase();
        case 'transcribed': return TRANSCRIBED_RANK[c.transcriptionStatus] ?? 0;
        case 'match':       return MATCH_STATUS_RANK[c.matchStatus] ?? 99;
        case 'uploader':    return (c.uploaderName || '').toLowerCase();
        case 'date':        return c.createdAt || '';
        default:            return '';
      }
    };
    return [...footage].sort((a, b) => {
      const av = key(a), bv = key(b);
      if (av < bv) return -1 * dir;
      if (av > bv) return  1 * dir;
      return 0;
    });
  }, [footage, sort]);

  const onSort = col => setSort(prev => (prev.col === col
    ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
    : { col, dir: col === 'date' ? 'desc' : 'asc' }));

  // ── Header ──────────────────────────────────────────────────────────────────

  const filtersOn = !!(clientFilter || statusFilter || uploaderFilter);
  const clearFilters = () => { setClientFilter(''); setStatusFilter(''); setUploaderFilter(''); };

  usePageHeader({
    title: 'Raw footage',
    meta: isExternal ? undefined : loading ? 'Loading' : plural(total, 'clip'),
    actions: (
      <>
        {canIngest && (
          <Link to="/footage/ingest" className="ui ui-btn ui-btn--secondary" data-testid="open-camera-ingest">
            <Icon icon={HardDrive} />Ingest from camera drive
          </Link>
        )}
        {canUpload && <Button variant="primary" icon={Upload} onClick={() => setUploadOpen(true)}>Upload footage</Button>}
      </>
    ),
    filters: (
      <>
        <div className="ui-seg" role="group" aria-label="Match status">
          {STATUS_FILTERS.map(p => (
            <Button key={p.value} variant="quiet" icon={p.value === 'archived' ? Archive : undefined}
              data-testid="footage-status-pill" data-value={p.value || 'all'} aria-pressed={statusFilter === p.value}
              onClick={() => setStatusFilter(p.value)}>
              {p.label}
            </Button>
          ))}
        </div>
        {!isExternal && (
          <Select aria-label="Project" value={clientFilter} onChange={e => setClientFilter(e.target.value)}
            options={[{ value: '', label: 'All projects' }, { value: 'unassigned', label: 'Unassigned' }, ...clients.map(c => ({ value: c.id, label: clean(c.name) }))]} />
        )}
        <Select aria-label="Uploader" value={uploaderFilter} onChange={e => setUploaderFilter(e.target.value)}
          options={[{ value: '', label: 'All uploaders' }, ...members.map(m => ({ value: m.id, label: clean(m.name) }))]} />
        {filtersOn && <Button variant="quiet" icon={X} onClick={clearFilters}>Clear</Button>}
      </>
    ),
  });

  // ── Grid ────────────────────────────────────────────────────────────────────

  const allSelected = sortedFootage.length > 0 && sortedFootage.every(c => selectedClips.has(c.id));
  const head = (col, label) => <SortHead col={col} label={label} sort={sort} onSort={onSort} />;
  const columns = [
    {
      key: 'select', width: 56,
      label: <Checkbox label={<span className="ui-sr">Select every clip in the view</span>} checked={allSelected}
        onChange={e => setSelectedClips(e.target.checked ? new Set(sortedFootage.map(c => c.id)) : new Set())} />,
      render: c => (
        <span onClick={stop}>
          <Checkbox label={<span className="ui-sr">Select {clean(c.fileName)}</span>} checked={selectedClips.has(c.id)} onChange={() => toggleClipSelect(c.id)} />
        </span>
      ),
    },
    { key: 'preview', width: 80, label: <span className="ui-sr">Preview</span>, render: c => <Thumb clip={c} /> },
    { key: 'file', width: 300, label: head('file', 'File'), render: c => (
      <span className="ui-cluster ui-cluster--nowrap">
        {c.parentFootageId && <><span className="ui-indent" /><Icon icon={CornerDownRight} label="Scene clip" /></>}
        {c.sceneLabel && <Tag>{clean(c.sceneLabel)}</Tag>}
        <span className="ui-truncate" title={clean(c.fileName)}>{clean(c.fileName)}</span>
        {c.clipCount > 0 && <Tag title="Scene clips extracted from this footage">{plural(c.clipCount, 'clip')}</Tag>}
        {c.isPossibleDuplicate && (
          <span title={c.duplicateOfName ? `Possible duplicate of ${clean(c.duplicateOfName)}` : 'Possible duplicate upload'}><Badge tone="warn">Duplicate?</Badge></span>
        )}
        {isUnverified(c) && <span title={clean(c.verificationError) || 'Upload failed verification'}><Badge tone="danger">Unverified</Badge></span>}
      </span>
    ) },
    { key: 'project', width: 150, label: head('project', 'Project'), render: c => (c.clientName ? <Tag>{clean(c.clientName)}</Tag> : <span className="ui-meta">Unassigned</span>) },
    { key: 'transcribed', width: 140, label: head('transcribed', 'Transcribed'), render: c => {
      const tx = c.transcriptionStatus;
      if (tx === 'complete') return <span>Yes</span>;
      if (tx === 'pending' || tx === 'processing') return <Badge tone="info">Transcribing</Badge>;
      const isRetry = tx === 'failed';
      return (
        <span onClick={stop}>
          <Button variant="quiet" icon={isRetry ? RotateCw : FileText} loading={transcribingIds.has(c.id)}
            aria-label={isRetry ? `Retry the transcription of ${clean(c.fileName)}` : `Transcribe ${clean(c.fileName)}`}
            onClick={() => handleTranscribeRow(c.id)}>
            {isRetry ? 'Retry' : 'Transcribe'}
          </Button>
        </span>
      );
    } },
    { key: 'match', width: 150, label: head('match', 'Match status'), render: c => (
      <span data-testid="footage-row" data-match-status={c.matchStatus || ''}><MatchStatus clip={c} /></span>
    ) },
    { key: 'uploader', width: 140, label: head('uploader', 'Uploader'), render: c => (c.uploaderName ? clean(c.uploaderName) : <span className="ui-meta">Unknown</span>) },
    { key: 'date', width: 116, label: head('date', 'Date'), render: c => fmtDate(c.createdAt) },
    { key: 'size', width: 90, label: 'Size', render: c => <span className="ui-tc">{formatBytes(c.fileSizeBytes)}</span> },
  ];

  const empty = isExternal
    ? { message: 'Footage you upload goes to the production team for review. Upload a clip to send it.', action: canUpload ? { label: 'Upload footage', icon: Upload, onClick: () => setUploadOpen(true) } : undefined }
    : filtersOn
      ? { message: 'No clip matches these filters. Clear them to see every clip.', action: { label: 'Clear filters', icon: X, onClick: clearFilters } }
      : canUpload
        ? { message: 'No footage yet. Upload the first clip, or ingest a camera drive.', action: { label: 'Upload footage', icon: Upload, onClick: () => setUploadOpen(true) } }
        : { message: 'No footage yet. Clips appear here once the team uploads them.' };

  return (
    <div className="ui ui-page ui-page--flush">
      {selectedClips.size > 0 && (
        <div className="ui-inset ui-inset--bar">
          <div className="ui-toolbar" role="group" aria-label="Selected clips">
            <span className="ui-meta">{plural(selectedClips.size, 'clip')} selected</span>
            <Button variant="danger" icon={Trash2} loading={bulkDeleting} onClick={askBulkDelete}>Delete {plural(selectedClips.size, 'clip')}</Button>
            <Button variant="quiet" disabled={bulkDeleting} onClick={() => setSelectedClips(new Set())}>Clear selection</Button>
          </div>
        </div>
      )}
      {bulkReport && (
        <div className="ui-inset ui-inset--bar">
          <section className="ui-region ui-region--pad ui-stack" aria-labelledby="footage-bulk-report" role="status">
            <div className="ui-section__head ui-section__head--center">
              <h2 className="ui-heading" id="footage-bulk-report">
                {bulkReport.deleted} of {bulkReport.total} deleted, {bulkReport.skipped.length} skipped
              </h2>
              <span className="ui-section__actions">
                <Button variant="quiet" iconOnly icon={X} label="Dismiss the delete report" onClick={() => setBulkReport(null)} />
              </span>
            </div>
            <ul className="ui-plainlist ui-scroll ui-stack ui-stack--tight">
              {bulkReport.skipped.map(s => (
                <li key={s.id} className="ui-dense"><span className="ui-text-2">{clean(s.name)}</span>: {clean(s.reason)}</li>
              ))}
            </ul>
          </section>
        </div>
      )}
      <div className="ui-work">
        <Grid
          label="Raw footage"
          columns={columns} rows={sortedFootage} rowKey={r => r.id}
          density="medium" storageKey="hf-grid:footage" selectedKey={selectedId}
          onOpen={c => setSelectedId(prev => (prev === c.id ? null : c.id))}
          state={loadError ? 'error' : loading ? 'loading' : 'data'}
          empty={empty}
          error={{ title: 'The footage did not load', message: 'Check your connection, then try again.', onRetry: () => fetchFootage() }} />
        {selectedId && (
          <ClipPanel
            key={selectedId}
            footageId={selectedId}
            clients={clients}
            onClose={() => setSelectedId(null)}
            onUpdated={refreshList}
            onOpenFootage={id => setSelectedId(id)}
          />
        )}
      </div>

      {canUpload && <UploadModal open={uploadOpen} onClose={() => setUploadOpen(false)} clients={clients} user={user} />}
    </div>
  );
}

// ── Upload: pick the project, then every file goes to the global Upload sheet ──

function UploadModal({ open, onClose, clients, user }) {
  const toast = useToast();
  const input = useRef(null);
  const [over, setOver] = useState(false);
  const [clientId, setClientId] = useState('');
  const isExternal = !!user?.isExternal;
  const assigned = user?.assignedClientIds || [];
  const effectiveClientId = isExternal && assigned.length ? assigned[0] : clientId;
  // External accounts always upload into their assigned project; internal users may leave it unassigned and tag it later.
  const blocked = isExternal && !effectiveClientId;

  function take(fileList) {
    if (blocked) return;
    const files = [...(fileList || [])];
    const accepted = files.filter(f => isMediaFile(f.name));
    const skipped = files.length - accepted.length;
    if (skipped) toast(`${plural(skipped, 'file')} skipped: not a supported video or audio format.`, { tone: 'warn' });
    if (!accepted.length) return;
    for (const file of accepted) {
      enqueueUpload({
        file,
        endpoints: FOOTAGE_ENDPOINTS,
        startBody: { clientId: effectiveClientId },
        idFieldName: 'footageId',
        meta: { kind: 'footage', clientId: effectiveClientId },
        // /confirm HEADs the object in S3 and checks it matches the source's size before the clip is usable.
        confirm: async (footageId, f) => {
          const { data } = await api.post('/api/footage/confirm', { footageId });
          if (data?.verified === false) {
            listRefresh.current?.();
            throw new Error(clean(data?.verification?.error) || 'The uploaded file does not match the source. Upload it again.');
          }
          if (data?.isPossibleDuplicate) {
            toast(`${clean(f.name)} may be a duplicate${data.duplicateOfName ? ` of ${clean(data.duplicateOfName)}` : ''}. It was kept and flagged for review.`, { tone: 'warn' });
          }
          return { ...data, footageId };
        },
      });
    }
    toast(`${plural(accepted.length, 'file')} added to the upload sheet. You can keep working while they upload.`);
    onClose();
  }

  return (
    <Modal open={open} onClose={onClose} title="Upload footage"
      footer={<Button variant="quiet" onClick={onClose}>Close</Button>}>
      {blocked ? (
        <p className="ui-p ui-dense ui-text-2" role="alert">
          Your account has no project assigned, so footage cannot be uploaded yet. Ask your producer to assign your project, then try again.
        </p>
      ) : (
        <div className="ui-stack">
          {!isExternal && (
            <Select label="Project" value={clientId} onChange={e => setClientId(e.target.value)}
              hint="Optional. Assign it now or while reviewing the clip."
              options={[{ value: '', label: 'No project, tag it later' }, ...clients.map(c => ({ value: c.id, label: clean(c.name) }))]} />
          )}
          <div className="ui-dropzone" data-drop={over || undefined}
            onDragOver={e => { e.preventDefault(); setOver(true); }}
            onDragLeave={() => setOver(false)}
            onDrop={e => { e.preventDefault(); setOver(false); take(e.dataTransfer.files); }}>
            <span className="ui-dense ui-text-2">Drop footage or audio here, or choose the files. {SUPPORTED}</span>
            <Button variant="secondary" icon={Upload} onClick={() => input.current?.click()}>Choose files</Button>
            <input ref={input} type="file" accept={ACCEPT_ATTR} multiple hidden onChange={e => { take(e.target.files); e.target.value = ''; }} />
          </div>
        </div>
      )}
    </Modal>
  );
}

// ── Script preview: read-only for a pending match, preview-then-assign from the script search ──

const STOP_WORDS = new Set(['that','this','with','from','have','been','will','were','they','their','when','what','which','your','into','than','then','also','more','about','some','would','could','should','there','these','those','other','after','before','where','while','being','just','like','over','even','only','such','very','each','both','most','here','well','much','many','same','made','make','take','come','know','time','look','said','want','need','good','back']);

function buildHighlightRegex(transcriptText) {
  if (!transcriptText) return null;
  const words = transcriptText
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(w => w.length > 4 && !STOP_WORDS.has(w));
  const unique = [...new Set(words)].slice(0, 30);
  if (!unique.length) return null;
  return new RegExp(`(${unique.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi');
}

// Script text with URLs as links and the words that also occur in the transcript marked. Non-strings render as text.
function HighlightedText({ text, regex }) {
  if (typeof text !== 'string') text = text == null ? '' : String(text);
  if (!text) return null;
  const urlParts = text.split(/(https?:\/\/[^\s<>"')]+)/g);
  return (
    <>
      {urlParts.map((part, i) => {
        if (!part) return null;
        if (/^https?:\/\//.test(part)) {
          return <a key={i} className="ui-anywhere" href={part} target="_blank" rel="noreferrer noopener">{part}</a>;
        }
        const display = clean(part);
        if (!regex) return <span key={i}>{display}</span>;
        const segments = display.split(new RegExp(regex.source, regex.flags));
        return (
          <span key={i}>
            {segments.map((seg, j) => (new RegExp(regex.source, regex.flags).test(seg)
              ? <mark key={j} className="ui-hl">{seg}</mark>
              : <span key={j}>{seg}</span>))}
          </span>
        );
      })}
    </>
  );
}

const sentenceCase = k => { const s = k.replace(/([A-Z])/g, ' $1').trim().toLowerCase(); return s.charAt(0).toUpperCase() + s.slice(1); };

function ScriptPreview({ scriptId, transcriptText, onConfirm, confirmLabel, confirming, onClose }) {
  const [script, setScript] = useState(null);
  const [loading, setLoading] = useState(true);
  const hlRegex = useMemo(() => buildHighlightRegex(transcriptText), [transcriptText]);

  useEffect(() => {
    api.get(`/api/scripts/${scriptId}`)
      .then(r => setScript(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [scriptId]);

  const scenes = script?.scriptBody?.scenes || [];
  // Columns from the first scene's keys, minus row metadata: id, the per-row qualNotes, and any underscore key (objects).
  const colHeaders = scenes.length > 0
    ? Object.keys(scenes[0]).filter(k => k !== 'qualNotes' && k !== 'id' && !k.startsWith('_'))
    : [];
  const close = () => { if (!confirming) onClose(); };

  return createPortal(
    <Modal open size="wide" onClose={close} title={loading ? 'Loading the script' : script ? clean(script.scriptName || 'Untitled') : 'Script not found'}
      footer={onConfirm ? (
        <>
          <Button variant="quiet" disabled={confirming} onClick={close}>Cancel</Button>
          <Button variant="primary" icon={Check} loading={confirming} disabled={loading || !script} onClick={onConfirm}>{confirmLabel || 'Use this script'}</Button>
        </>
      ) : <Button variant="quiet" onClick={close}>Close</Button>}>
      <div className="ui-stack">
        <span className="ui-meta">
          {onConfirm ? 'Preview. Assign it to link this footage to the script.' : 'Preview, read only.'}
          {script ? ` ${joinMeta([script.client?.name || script.clientName, script.scriptBody?.format || script.scriptType, script.qualStatus])}` : ''}
        </span>
        {hlRegex && <span className="ui-dense ui-text-2">Marked words also occur in the transcript.</span>}
        {loading && <><Skeleton width="80%" /><Skeleton width="60%" /><Skeleton variant="block" height={160} /></>}
        {!loading && !script && <p className="ui-p ui-dense ui-text-2">This script could not be loaded. It may have been deleted.</p>}
        {!loading && script && scenes.length === 0 && <p className="ui-p ui-dense ui-text-2">This script has no content yet.</p>}
        {!loading && script && scenes.length > 0 && (
          <div className="ui ui-table-wrap">
            <table className="ui-table">
              <thead><tr>{colHeaders.map(h => <th key={h} scope="col">{sentenceCase(h)}</th>)}</tr></thead>
              <tbody>
                {scenes.map((scene, i) => (
                  <tr key={i}>
                    {colHeaders.map(col => <td key={col} className="ui-cell-wrap"><HighlightedText text={scene[col] || ''} regex={hlRegex} /></td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>,
    document.body,
  );
}

// ── Script search: every script and riff card, across all projects ──

function ScriptSearch({ clientId, onPick }) {
  const [term, setTerm]       = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen]       = useState(false);
  const [loading, setLoading] = useState(false);
  const [picking, setPicking] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const q = term.trim();
    if (q.length < 2) { setResults([]); setLoading(false); return undefined; }
    setLoading(true);
    const handle = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ q });
        if (clientId) params.set('clientId', clientId);
        const r = await api.get(`/api/footage/scripts-search?${params.toString()}`);
        setResults(Array.isArray(r.data) ? r.data.slice(0, 8) : []);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(handle);
  }, [term, clientId]);

  useEffect(() => {
    function onDocClick(e) { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  async function pick(result) {
    setPicking(true);
    try { await onPick(result); }
    finally { setPicking(false); setOpen(false); setTerm(''); setResults([]); }
  }

  return (
    <div ref={boxRef} className="ui-stack ui-stack--tight">
      <Input type="search" icon={Search} aria-label="Search scripts or riff cards" value={term} disabled={picking}
        placeholder={clientId ? 'Search scripts or riff cards' : 'Search scripts or riff cards in every project'}
        onChange={e => { setTerm(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} />
      {open && term.trim().length >= 2 && (
        <div className="ui-region ui-list ui-scroll" role="group" aria-label="Search results">
          {loading && <p className="ui-p ui-dense ui-text-3 ui-region--pad" role="status">Searching</p>}
          {!loading && results.length === 0 && <p className="ui-p ui-dense ui-text-3 ui-region--pad">No script or riff card matches. Try another word from the title.</p>}
          {!loading && results.map(s => {
            const isRiff = s.type === 'riff';
            const key   = isRiff ? `riff-${s.liveProductionId}` : `script-${s.id}`;
            const title = isRiff ? (clean(s.title?.trim()) || 'Untitled riff') : (clean(s.scriptName) || 'Untitled');
            const sub   = isRiff ? joinMeta([s.client?.name, s.actorName, s.status]) : joinMeta([s.client?.name, s.format, s.qualStatus]);
            return (
              <ListRow key={key} onClick={picking ? undefined : () => pick(s)}
                leading={isRiff ? <Tag>Riff</Tag> : <Icon icon={FileText} />}
                title={title} meta={sub} />
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Clip panel (the Expanded-row panel; ?footage=<id> selects it) ──

function ClipPanel({ footageId, clients = [], onClose, onUpdated, onOpenFootage }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const confirm = useConfirm();
  const [footage,       setFootage]       = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [transcribing,  setTranscribing]  = useState(false);
  const [previewMatch,  setPreviewMatch]  = useState(null);
  const [searchPreview, setSearchPreview] = useState(null);   // { scriptId }: preview-then-assign from the search
  const [confirmingSearch, setConfirmingSearch] = useState(false);
  const [unassigning,   setUnassigning]   = useState(false);
  // Archive MOVES the file to cold Storage, so it gets its own confirm with the server's answer for where it would go.
  const [archiveConfirm, setArchiveConfirm] = useState(null);
  const [deleting,      setDeleting]      = useState(false);
  const [archiving,     setArchiving]     = useState(false);
  const [editingClient, setEditingClient] = useState(false);
  const [clientChoice,  setClientChoice]  = useState('');
  const [savingClient,  setSavingClient]  = useState(false);
  const [rematching,    setRematching]    = useState(false);
  const [savingOrig,    setSavingOrig]    = useState(false);
  // ADC (Automated Dailies Cutter): { adc_status, adc_parsed_title, adc_error, adc_completed_at, clips }
  const [adc,           setAdc]           = useState(null);
  const [adcStarting,   setAdcStarting]   = useState(false);
  const [clipsOpen,     setClipsOpen]     = useState(true);
  const txPollRef  = useRef(null);
  const adcPollRef = useRef(null);
  const videoRef   = useRef(null);
  // Faces (D-153 / D-154): the eye's person tags on this clip and the client's character registry.
  const [faceChars, setFaceChars] = useState(null);

  const canEdit = hasAnyRole(user, EDIT_ROLES);
  const isAdmin = hasAnyRole(user, ADMIN_ROLES);
  const isUploader = !!user?.id && !!footage?.uploadedBy && footage.uploadedBy === user.id;
  const fail = (err, fallback) => toast(clean(err?.response?.data?.error) || fallback, { tone: 'danger' });

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/api/footage/${footageId}`);
      setFootage(r.data);
    } catch { /* the panel says it could not load */ }
    finally { setLoading(false); }
  }, [footageId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!footage?.visualFaces?.length || !footage?.registryClientId || !footage?.canNameFaces) return;
    api.get(`/api/client-characters?clientId=${footage.registryClientId}`).then(r => setFaceChars((r.data?.characters || []).filter(c => !c.isArchived))).catch(() => setFaceChars([]));
  }, [footage?.registryClientId, footage?.visualFaces?.length, footage?.canNameFaces]);
  const seekPlayer = (t) => { const v = videoRef.current; if (!v) return; try { v.currentTime = t; v.pause(); v.scrollIntoView({ block: 'nearest' }); } catch { /* not seekable yet */ } };
  const faceSaver = useFaceSave(footageId, faces => setFootage(prev => prev ? { ...prev, visualFaces: faces } : prev));

  // Poll the transcription while it runs.
  useEffect(() => {
    const tx = footage?.transcription;
    if (tx?.status === 'pending' || tx?.status === 'processing') {
      txPollRef.current = setInterval(async () => {
        try {
          const r = await api.get(`/api/transcriptions/raw_footage/${footageId}`);
          if (r.data?.status !== 'pending' && r.data?.status !== 'processing') clearInterval(txPollRef.current);
          setFootage(prev => prev ? { ...prev, transcription: r.data } : prev);
        } catch { /* next tick */ }
      }, 8000);
    }
    return () => clearInterval(txPollRef.current);
  }, [footage?.transcription?.status, footageId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadAdc = useCallback(async () => {
    try {
      const r = await api.get(`/api/adc/${footageId}/status`);
      setAdc(r.data);
      return r.data;
    } catch { return null; }
  }, [footageId]);

  // ADC state, once we know this is a parent (not a scene clip).
  useEffect(() => {
    if (footage && !footage.parentFootageId) loadAdc();
  }, [footage?.id, footage?.parentFootageId, loadAdc]); // eslint-disable-line react-hooks/exhaustive-deps

  // Poll every 5 s while a job is queued or running; stop on a terminal state.
  useEffect(() => {
    const st = adc?.adc_status;
    if (st === 'pending' || st === 'processing') {
      adcPollRef.current = setInterval(async () => {
        const data = await loadAdc();
        if (data && data.adc_status !== 'pending' && data.adc_status !== 'processing') {
          clearInterval(adcPollRef.current);
          onUpdated?.();   // the list's clip-count tag updates
        }
      }, 5000);
    }
    return () => clearInterval(adcPollRef.current);
  }, [adc?.adc_status, loadAdc, onUpdated]);

  async function handleConfirmMatch(matchId) {
    try {
      await api.patch(`/api/footage/${footageId}/matches/${matchId}/confirm`);
      await load();
      onUpdated();
    } catch (err) { fail(err, 'The match was not confirmed. Try again.'); }
  }

  async function handleRejectMatch(matchId) {
    try {
      await api.patch(`/api/footage/${footageId}/matches/${matchId}/reject`);
      await load();
    } catch (err) { fail(err, 'The match was not rejected. Try again.'); }
  }

  async function handleNoMatch() {
    try {
      await api.post(`/api/footage/${footageId}/no-match`);
      await load();
      onUpdated();
    } catch (err) { fail(err, 'The clip was not marked as no match. Try again.'); }
  }

  async function handleManualMatchConfirm() {
    if (!searchPreview?.scriptId) return;
    setConfirmingSearch(true);
    try {
      await api.post(`/api/footage/${footageId}/manual-match`, { scriptId: searchPreview.scriptId });
      setSearchPreview(null);
      await load();
      onUpdated();
      toast('Script assigned to this clip.');
    } catch (err) { fail(err, 'The script was not linked. Try again.'); }
    finally { setConfirmingSearch(false); }
  }

  // The search box: scripts go through preview-then-assign; riff cards (no transcript to preview) link after a confirm.
  async function handleSearchPick(result) {
    if (result.type === 'riff') {
      const label = clean(result.title?.trim()) || 'Untitled riff';
      const ok = await confirm({
        title: 'Link this footage to the riff card?',
        body: `${label}${result.client?.name ? ` (${clean(result.client.name)})` : ''}. This replaces any current match.`,
        confirmLabel: 'Link riff card',
      });
      if (!ok) return;
      try {
        await api.post(`/api/footage/${footageId}/manual-match`, { liveProductionId: result.liveProductionId });
        await load();
        onUpdated();
        toast('Riff card linked to this clip.');
      } catch (err) { fail(err, 'The riff card was not linked. Try again.'); }
    } else {
      setSearchPreview({ scriptId: result.id });
    }
  }

  async function handleUnassign() {
    const ok = await confirm({
      title: 'Unlink this match?',
      body: 'You can then pick a different script or riff card.',
      confirmLabel: 'Unlink',
    });
    if (!ok) return;
    setUnassigning(true);
    try {
      await api.post(`/api/footage/${footageId}/unassign`);
      await load();
      onUpdated();
    } catch (err) { fail(err, 'The match was not unlinked. Try again.'); }
    finally { setUnassigning(false); }
  }

  async function handleTranscribe() {
    setTranscribing(true);
    try {
      await api.post('/api/transcriptions', { source_type: 'raw_footage', source_id: footageId });
      await load();
    } catch (err) { fail(err, 'The transcription did not start. Try again.'); }
    finally { setTranscribing(false); }
  }

  async function handleProcessClips() {
    setAdcStarting(true);
    try {
      await api.post(`/api/adc/${footageId}/process`);
      await loadAdc();   // picks up 'pending', which starts the poll
    } catch (err) {
      fail(err, 'Clip processing did not start. Try again.');
    } finally { setAdcStarting(false); }
  }

  async function handleDownload() {
    try {
      const r = await api.get(`/api/footage/${footageId}/download`);
      window.open(r.data.url, '_blank');
    } catch { toast('The download did not start. Try again.', { tone: 'danger' }); }
  }

  function startEditClient() {
    setClientChoice(footage?.clientId || '');
    setEditingClient(true);
  }

  async function handleRematch() {
    setRematching(true);
    try {
      await api.post(`/api/footage/${footageId}/match`);
      await load();
      onUpdated();
    } catch (err) {
      fail(err, 'The match did not run. Try again.');
    } finally {
      setRematching(false);
    }
  }

  // BRAW originals (B-3): this MP4 is a proxy of <same basename>.braw; the cut-list download names the original.
  async function handleOriginalExt(checked) {
    setSavingOrig(true);
    try { await api.patch(`/api/footage/${footageId}`, { originalExt: checked ? 'braw' : null }); await load(); onUpdated(); }
    catch (err) { fail(err, 'The original format was not saved. Try again.'); }
    finally { setSavingOrig(false); }
  }

  async function handleSaveClient() {
    const nextClientId = clientChoice || null;
    if (nextClientId === (footage?.clientId || null)) {
      setEditingClient(false);
      return;
    }
    setSavingClient(true);
    try {
      await api.patch(`/api/footage/${footageId}`, { clientId: nextClientId });
      setEditingClient(false);
      await load();
      onUpdated();
    } catch (err) {
      fail(err, 'The project did not change. Try again.');
    } finally {
      setSavingClient(false);
    }
  }

  async function handleDelete() {
    const ok = await confirm({
      title: 'Delete this clip permanently?',
      body: `${clean(footage.fileName)} is removed from cloud storage and its match history is discarded. This cannot be undone.`,
      confirmLabel: 'Delete clip',
      destructive: true,
    });
    if (!ok) return;
    setDeleting(true);
    try {
      await api.delete(`/api/footage/${footageId}`);
      onUpdated();
      onClose();
      toast(`${clean(footage.fileName)} deleted.`);
    } catch (err) {
      setDeleting(false);
      fail(err, 'The clip was not deleted. Try again.');
    }
  }

  // Ask the server where this clip would go, then show the confirm, so it names the real client and folder.
  async function openArchiveConfirm() {
    setArchiving(true);
    try {
      const res = await api.get(`/api/footage/${footageId}/archive-preview`);
      setArchiveConfirm(res.data);
    } catch (err) {
      fail(err, 'Could not check where this clip would be archived. Try again.');
    } finally {
      setArchiving(false);
    }
  }

  async function handleArchive() {
    setArchiving(true);
    try {
      await api.patch(`/api/footage/${footageId}/archive`);
      setArchiveConfirm(null);
      onUpdated();
      onClose();   // it leaves the active list
      toast('Clip archived to Storage.');
    } catch (err) {
      // Linked footage and every verification failure come back with a reason. A failure means nothing moved.
      fail(err, 'The clip was not archived. Nothing was moved. Try again.');
      setArchiveConfirm(null);
    } finally {
      setArchiving(false);
    }
  }

  async function handleUnarchive() {
    setArchiving(true);
    try {
      await api.patch(`/api/footage/${footageId}/unarchive`);
      await load();
      onUpdated();
      toast('Clip back in the active list.');
    } catch (err) {
      fail(err, 'The clip was not unarchived. Try again.');
    } finally {
      setArchiving(false);
    }
  }

  if (loading) {
    return (
      <ExpandedRowPanel open title="Loading the clip" onClose={onClose} label="Clip">
        <div className="ui-stack"><Skeleton width="60%" /><Skeleton variant="block" height={180} label="Loading the clip" /><Skeleton width="40%" /></div>
      </ExpandedRowPanel>
    );
  }
  if (!footage) {
    return (
      <ExpandedRowPanel open title="Clip not found" onClose={onClose} label="Clip">
        <p className="ui-p ui-dense ui-text-2">This clip could not be loaded. It may have been deleted; close the panel and refresh the list.</p>
      </ExpandedRowPanel>
    );
  }

  const tx = footage.transcription;
  const isAudio = mediaKindOf(footage) === 'audio';
  const isParent = footage.parentFootageId == null;
  // The file's bytes moved to the Storage vault: the row is kept, but nothing hot is left to play.
  const isColdArchived = !!footage.archivedToArchiveId;
  const pendingMatches = (footage.matches || []).filter(m => m.status === 'pending_review');
  const confirmedMatch = (footage.matches || []).find(m => m.status === 'confirmed');
  // Footage linked to a scriptless riff card has no match row; it is shown on its own.
  const linkedRiff = !confirmedMatch ? footage.linkedCard : null;
  const adcStatus = adc?.adc_status ?? footage.adcStatus ?? null;
  const txReady = tx?.status === 'complete';
  const canDelete = isAdmin || isUploader;

  return (
    <ExpandedRowPanel open title={clean(footage.fileName)} onClose={onClose} label="Clip"
      footer={(canEdit || canDelete) && (
        <>
          {canEdit && (footage.isArchived ? (
            <Button variant="secondary" icon={ArchiveRestore} loading={archiving} disabled={isColdArchived} onClick={handleUnarchive}
              title={isColdArchived ? 'This clip\'s file is in cold Storage. Restore it from Storage with Promote.' : 'Send this clip back to the active Raw footage list'}>
              Unarchive
            </Button>
          ) : (
            <Button variant="secondary" icon={Archive} loading={archiving} onClick={openArchiveConfirm}
              title={footage.linkedVideoCardId ? 'Linked to a video card: it archives on its own once that video is Posted' : 'Move this clip\'s file to cold Storage'}>
              Archive
            </Button>
          ))}
          {canDelete && <Button variant="danger" icon={Trash2} loading={deleting} onClick={handleDelete}>Delete</Button>}
        </>
      )}>
      <div className="ui-stack">
        {footage.parentFootageId && (
          <span>
            <Button variant="quiet" icon={ArrowLeft} onClick={() => onOpenFootage?.(footage.parentFootageId)} title="Back to the source footage">
              Back to {clean(footage.parentFileName) || 'the source footage'}
            </Button>
          </span>
        )}
        <dl className="ui-kv">
          <dt>Kind</dt><dd>{footage.parentFootageId ? 'Scene clip' : 'Raw footage'}</dd>
          <dt>Match status</dt><dd><MatchStatus clip={footage} /></dd>
          <dt>Project</dt>
          <dd>
            {editingClient ? (
              <span className="ui-stack ui-stack--tight">
                <Select aria-label="Project" value={clientChoice} disabled={savingClient} onChange={e => setClientChoice(e.target.value)}
                  options={[{ value: '', label: 'Unassigned' }, ...clients.map(c => ({ value: c.id, label: clean(c.name) }))]} />
                <span className="ui-cluster">
                  <Button variant="primary" loading={savingClient} onClick={handleSaveClient}>Save</Button>
                  <Button variant="quiet" disabled={savingClient} onClick={() => setEditingClient(false)}>Cancel</Button>
                </span>
                <span className="ui-meta">Changing the project clears the matches and runs auto-match again.</span>
              </span>
            ) : (
              <span className="ui-cluster">
                <span>{footage.clientName ? clean(footage.clientName) : 'Unassigned'}</span>
                {canEdit && <Button variant="quiet" onClick={startEditClient} title="Change the project (clears the matches and runs auto-match again)">Change</Button>}
              </span>
            )}
          </dd>
          <dt>Size</dt><dd className="ui-tc">{formatBytes(footage.fileSizeBytes) || 'Unknown'}</dd>
        </dl>

        {isUnverified(footage) && (
          <div className="ui-region ui-region--pad ui-stack ui-stack--tight" role="alert">
            <span><Badge tone="danger">Upload failed verification</Badge></span>
            <p className="ui-p ui-dense ui-text-2">{clean(footage.verificationError) || 'The stored file does not match the source. Upload this clip again.'}</p>
          </div>
        )}
        {footage.isPossibleDuplicate && (
          <div className="ui-region ui-region--pad ui-stack ui-stack--tight">
            <span><Badge tone="warn">Possible duplicate</Badge></span>
            <p className="ui-p ui-dense ui-text-2">
              This clip was uploaded under a name already in use{footage.duplicateOfName ? <>, likely a duplicate of <strong>{clean(footage.duplicateOfName)}</strong></> : ''}, and was renamed. Review it and delete it if it is the same footage.
            </p>
          </div>
        )}
        {footage.sourceArchiveId && (
          <div className="ui-cluster">
            <span className="ui-meta">This clip came from the Storage archive.</span>
            <Link className="ui ui-btn ui-btn--quiet" to={`/storage?fileId=${footage.sourceArchiveId}`}><Icon icon={Archive} />View the archived copy</Link>
          </div>
        )}

        {/* Archived to cold Storage: said where the player was, rather than a player that never loads. */}
        {isColdArchived && (
          <div className="ui-region ui-region--pad ui-stack ui-stack--tight">
            <h3 className="ui-heading">Archived to Storage</h3>
            <p className="ui-p ui-dense ui-text-2">
              This clip&apos;s file was moved to cold Storage, so it no longer plays here. The transcript and match history below are intact. Use Promote in Storage to bring it back.
            </p>
            <span><Link className="ui ui-btn ui-btn--secondary" to={`/storage?fileId=${footage.archivedToArchiveId}`}><Icon icon={Archive} />Open it in Storage</Link></span>
          </div>
        )}

        {isColdArchived ? null : footage.playbackUrl && isAudio ? (
          <div className="ui-cluster ui-cluster--nowrap">
            <Thumb clip={footage} />
            <audio key={footage.playbackUrl} className="ui-audio" controls preload="metadata">
              <source src={footage.playbackUrl} />
              Your browser does not play this audio inline. Use Download to listen to the clip.
            </audio>
          </div>
        ) : footage.playbackUrl ? (
          <div className="ui-stack ui-stack--tight">
            <div className="ui ui-monitor">
              <div className="ui-monitor__frame ui-monitor__frame--16x9">
                <video key={footage.playbackUrl} ref={videoRef} className="ui-contain" data-testid="footage-player" controls preload="metadata" poster={footage.thumbnailUrl || undefined}>
                  <source src={footage.playbackUrl} />
                  Your browser does not play this video inline. Use Download to view the clip.
                </video>
              </div>
            </div>
            {footage.transcodingStatus && footage.transcodingStatus !== 'complete' && (
              <span className="ui-meta">
                {footage.transcodingStatus === 'pending' || footage.transcodingStatus === 'processing'
                  ? 'The web preview is still transcoding. The original format may not play in every browser.'
                  : footage.transcodingStatus === 'failed'
                    ? 'The web preview did not transcode. Playing the original file.'
                    : null}
              </span>
            )}
          </div>
        ) : footage.thumbnailUrl ? (
          <div className="ui ui-monitor">
            <div className="ui-monitor__frame ui-monitor__frame--16x9"><img className="ui-cover" src={footage.thumbnailUrl} alt="" /></div>
          </div>
        ) : null}

        <div className="ui-cluster">
          <Button variant="secondary" icon={Download} onClick={handleDownload}>Download</Button>
          {(!tx || tx.status === 'failed') && (
            <Button variant="secondary" icon={tx?.status === 'failed' ? RotateCw : FileText} loading={transcribing} onClick={handleTranscribe}>
              {tx?.status === 'failed' ? 'Retry transcription' : 'Transcribe'}
            </Button>
          )}
          {(tx?.status === 'pending' || tx?.status === 'processing') && <span role="status"><Badge tone="info">Transcribing</Badge></span>}
          {/* Share links: nothing at all for an external account (the component's own gate); no new link on a cold-archived clip. */}
          <ShareLinkManager
            resourceType="footage"
            resourceId={footageId}
            disabledReason={isColdArchived
              ? 'This clip has been archived to Storage, so its file is no longer here and cannot be shared. Restore it from Storage first.'
              : null}
          />
          {txReady && <Badge tone="ok">Transcript ready</Badge>}
          {canEdit && txReady && (
            <Button variant="quiet" icon={RefreshCw} loading={rematching} onClick={handleRematch}
              title="Clear the pending matches and run auto-match again against this clip's project">
              Run match again
            </Button>
          )}
          {/* Process clips (ADC): parents only, after the upload completes, never audio (ADC cuts video scene clips). */}
          {isParent && footage.uploadStatus === 'complete' && !isAudio && (
            adcStatus === 'pending' || adcStatus === 'processing'
              ? <span role="status"><Badge tone="info">Processing clips</Badge></span>
              : adcStatus === 'complete'
                ? <span className="ui-meta">Clips processed</span>
                : (
                  <Button variant="quiet" icon={Scissors} loading={adcStarting} disabled={!txReady}
                    title={txReady ? 'Extract scene clips from the director\'s action and cut cues' : 'Transcribe the clip first'}
                    onClick={handleProcessClips}>
                    Process clips
                  </Button>
                )
          )}
        </div>

        {canEdit && isParent && /\.mp4$/i.test(footage.fileName || '') && (
          <div className="ui-stack ui-stack--tight">
            <Checkbox label="Proxy, the original is BRAW" data-testid="footage-original-braw" checked={footage.originalExt === 'braw'} disabled={savingOrig}
              onChange={e => handleOriginalExt(e.target.checked)} />
            <span data-testid="footage-original-braw-help" className="ui-meta">
              The proxy must keep the original&apos;s exact name, frame rate and length. A renamed proxy cannot be pointed back at its original, and a different frame rate puts every cut in the wrong place.
            </span>
          </div>
        )}

        {footage.visualFaces?.length > 0 && (
          <section className="ui-section" aria-labelledby="clip-faces" data-testid="footage-faces">
            <h3 className="ui-heading" id="clip-faces">Name the faces</h3>
            <p className="ui-section__note">
              The people the eye found in this clip. A name here outranks every inferred binding. Director and Crew never count as a performer.
            </p>
            {footage.visualFaces.map(f => (
              <FaceCard key={f.tag} face={f} footageId={footageId} cast={footage.faceCast || []} chars={faceChars}
                canName={!!footage.canNameFaces} onSeek={seekPlayer} saver={faceSaver} />
            ))}
          </section>
        )}

        {isParent && (adc?.adc_status || adc?.clips?.length > 0) && (
          <section className="ui-section" aria-label="Scene clips">
            {adc?.adc_status === 'processing' && (
              <span className="ui-cluster" role="status"><Badge tone="info">Extracting scenes</Badge><span className="ui-meta">This can take a few minutes.</span></span>
            )}
            {adc?.adc_status === 'pending' && <span className="ui-meta" role="status">Queued for clip extraction.</span>}
            {adc?.adc_status === 'complete' && (
              <span className="ui-dense ui-text-2">
                {plural(adc.clips?.length || 0, 'clip')} extracted{adc.adc_parsed_title ? `: ${clean(adc.adc_parsed_title)}` : ''}
              </span>
            )}
            {adc?.adc_status === 'failed' && (
              <span className="ui-stack ui-stack--tight" role="alert">
                <span><Badge tone="danger">Clip extraction failed</Badge></span>
                {adc.adc_error && <span className="ui-dense ui-text-2">{clean(adc.adc_error)}</span>}
              </span>
            )}
            {adc?.clips?.length > 0 && (
              <>
                <h3 className="ui-heading">
                  <button type="button" className="ui-disclosure" aria-expanded={clipsOpen} onClick={() => setClipsOpen(o => !o)}>
                    <Icon icon={ChevronDown} />Scene clips<span className="ui-meta">{adc.clips.length}</span>
                  </button>
                </h3>
                {clipsOpen && (
                  <div className="ui-region ui-list">
                    {adc.clips.map(clip => (
                      <ListRow key={clip.id} onClick={() => onOpenFootage?.(clip.id)}
                        leading={<Thumb clip={clip} />} title={clean(clip.sceneLabel) || 'Scene'} meta={clean(clip.fileName)}
                        trailing={<span className="ui-meta">Open</span>} />
                    ))}
                  </div>
                )}
              </>
            )}
          </section>
        )}

        <section className="ui-section" aria-labelledby="clip-matches">
          <h3 className="ui-heading" id="clip-matches">Script matches</h3>

          {linkedRiff && (
            <div className="ui-region ui-region--pad ui-stack ui-stack--tight">
              <span className="ui-cluster"><Tag>Riff</Tag><Badge tone="ok">Linked to riff card</Badge></span>
              <span>{clean(linkedRiff.title?.trim()) || 'Untitled riff'}</span>
              <span className="ui-meta">{joinMeta([linkedRiff.clientName, linkedRiff.actorName, linkedRiff.status])}</span>
              <span className="ui-cluster">
                <Button variant="secondary" onClick={() => navigate(`/production/${linkedRiff.liveProductionId}`)}>Open production</Button>
                {canEdit && (
                  <Button variant="quiet" icon={Unlink} loading={unassigning} onClick={handleUnassign} title="Unlink this riff card so you can pick a different match">
                    Unlink or change
                  </Button>
                )}
              </span>
            </div>
          )}

          {confirmedMatch && (
            <div className="ui-region ui-region--pad ui-stack ui-stack--tight">
              <span><Badge tone="ok">Confirmed match</Badge></span>
              <span>{clean(confirmedMatch.scriptName)}</span>
              <span className="ui-meta">
                {joinMeta([confirmedMatch.clientName, Number(confirmedMatch.similarityScore) > 0 ? `${formatPct(confirmedMatch.similarityScore)} match` : 'linked by hand'])}
              </span>
              {(confirmedMatch.liveProductionId || canEdit) && (
                <span className="ui-cluster">
                  {confirmedMatch.liveProductionId && (
                    <Button variant="secondary" onClick={() => navigate(`/production/${confirmedMatch.liveProductionId}`)}>Open production</Button>
                  )}
                  {canEdit && (
                    <Button variant="quiet" icon={Unlink} loading={unassigning} onClick={handleUnassign} title="Unassign this script so you can pick a different one">
                      Unassign or change script
                    </Button>
                  )}
                </span>
              )}
            </div>
          )}

          {canEdit && pendingMatches.length > 0 && (
            <div className="ui-stack">
              {pendingMatches.map(m => (
                <div key={m.id} className="ui-region ui-region--pad ui-stack ui-stack--tight">
                  <span className="ui-cluster ui-cluster--nowrap">
                    <span className="ui-tc">#{m.rank}</span>
                    <span className="ui-truncate" title={clean(m.scriptName)}>{clean(m.scriptName)}</span>
                  </span>
                  {String(m.matchMethod || '').startsWith('action_cut') && (
                    <span><Tag>{`Matched on action and cut lines${m.matchMethod === 'action_cut_ai' ? ' (AI)' : ''}`}</Tag></span>
                  )}
                  <span className="ui-meta">{joinMeta([m.clientName, `${formatPct(m.similarityScore)} similarity`])}</span>
                  <span className="ui-cluster">
                    <Button variant="secondary" icon={Check} onClick={() => handleConfirmMatch(m.id)}>Confirm</Button>
                    <Button variant="quiet" icon={X} onClick={() => handleRejectMatch(m.id)}>Reject</Button>
                    <Button variant="quiet" icon={Eye} aria-pressed={previewMatch?.id === m.id} onClick={() => setPreviewMatch(previewMatch?.id === m.id ? null : m)}>Preview</Button>
                  </span>
                </div>
              ))}
              <span><Button variant="quiet" onClick={handleNoMatch}>Mark as no match</Button></span>
            </div>
          )}

          {!confirmedMatch && !linkedRiff && pendingMatches.length === 0 && (
            <p className="ui-p ui-dense ui-text-2">
              {footage.matchStatus === 'no_match' ? 'Marked as no match.'
                : !tx ? 'Transcribe this clip to find script matches automatically.'
                  : tx.status === 'complete' ? 'No matches found. Search for the script below.' : 'Matches appear once the transcription finishes.'}
            </p>
          )}

          {!canEdit && pendingMatches.length > 0 && (
            <p className="ui-p ui-dense ui-text-2">{plural(pendingMatches.length, 'match candidate')} waiting on review.</p>
          )}

          {/* Script search: open to editors in every match state. Preview a script against the transcript, then assign it
              to force-match this clip, even when it is already matched. */}
          {canEdit && (
            <div className="ui-stack ui-stack--tight">
              <h4 className="ui-heading">Script search</h4>
              <ScriptSearch clientId={null} onPick={handleSearchPick} />
              <span className="ui-meta">Search every script, preview it against the transcript, then assign it to force-match this clip, even if it is already matched.</span>
            </div>
          )}
        </section>

        {txReady && tx.transcriptText && (
          <section className="ui-section" aria-labelledby="clip-transcript">
            <h3 className="ui-heading" id="clip-transcript">Transcript</h3>
            <div className="ui-region ui-region--pad ui-scroll ui-prewrap ui-dense ui-text-2">{tx.transcriptText}</div>
            <TranscriptDownloads transcriptionId={tx.id} transcriptText={tx.transcriptText} baseName={footage.fileName} />
          </section>
        )}
      </div>

      {previewMatch && (
        <ScriptPreview scriptId={previewMatch.scriptId} transcriptText={tx?.transcriptText} onClose={() => setPreviewMatch(null)} />
      )}
      {searchPreview && (
        <ScriptPreview scriptId={searchPreview.scriptId} transcriptText={tx?.transcriptText}
          onConfirm={handleManualMatchConfirm} confirming={confirmingSearch} confirmLabel="Assign this script"
          onClose={() => setSearchPreview(null)} />
      )}
      {archiveConfirm && createPortal(
        <Modal open onClose={() => { if (!archiving) setArchiveConfirm(null); }} title="Archive to Storage?"
          footer={(
            <>
              <Button variant="quiet" disabled={archiving} onClick={() => setArchiveConfirm(null)}>Cancel</Button>
              <Button variant="primary" icon={Archive} loading={archiving} onClick={handleArchive}>Archive to Storage</Button>
            </>
          )}>
          <div className="ui-stack">
            <p className="ui-p">Move <strong>{clean(archiveConfirm.fileName)}</strong> to cold Storage.</p>
            <dl className="ui-kv">
              <dt>Destination</dt>
              <dd>{clean(archiveConfirm.clientName)}, <strong>{clean(archiveConfirm.folderName)}</strong></dd>
            </dl>
            <ul className="ui-plainlist ui-stack ui-stack--tight ui-dense">
              <li>The file moves to cold Storage (Glacier) and stops playing here.</li>
              <li>The clip, its transcript and its match history are kept.</li>
              <li>It can be restored from Storage at any time with Promote.</li>
            </ul>
            {/* Archiving breaks any live public link: warn with the count before, not after a viewer complains. */}
            {archiveConfirm.activeShareLinks > 0 && (
              <div className="ui-region ui-region--pad ui-stack ui-stack--tight">
                <span><Badge tone="warn">{plural(archiveConfirm.activeShareLinks, 'active share link')}</Badge></span>
                <p className="ui-p ui-dense ui-text-2">
                  Anyone holding {archiveConfirm.activeShareLinks === 1 ? 'that link' : 'those links'} sees &quot;this clip has been archived&quot; instead of the clip.
                  The link{archiveConfirm.activeShareLinks === 1 ? '' : 's'} work{archiveConfirm.activeShareLinks === 1 ? 's' : ''} again if you restore the clip from Storage.
                </p>
              </div>
            )}
          </div>
        </Modal>,
        document.body,
      )}
    </ExpandedRowPanel>
  );
}
